import { cx } from "@/lib/cx";

/**
 * The one description of what a text control looks like.
 *
 * Before this existed the same forty characters of Tailwind were pasted into
 * eight files, and they had already drifted into two backgrounds and four
 * heights — so a field in a dialog was 11 tall and a field on a panel was 10,
 * and neither lined up with the button beside it.
 *
 * Three axes now, and the two original ones are unchanged in meaning:
 *
 * - **`on`** — *what the field is sitting on*, not what colour to paint it. A
 *   field has to be distinguishable from its parent. The desktop pass gives
 *   panel, dialog and sheet fields one paper/well recipe; the grouped context
 *   remains the exception because its group card is already the field.
 * - **`size`** — the same three steps as `Button` (`h-8` / `h-10` / `h-12`), so
 *   a control and the button next to it are always the same height.
 * - **`variant`** — `filled` (the desktop field: a distinct tonal material) or
 *   `plain` (no fill at all, for a field that *is* a row in a grouped list).
 *
 * Anything that owns its own height — a textarea, an auto-growing field —
 * composes {@link controlBase} directly and adds its own padding.
 *
 * ---------------------------------------------------------------------------
 * WHY THE FIELD IS TONAL
 * ---------------------------------------------------------------------------
 * Material filled controls separate through a tonal container instead of a
 * private outline token. The field remains distinct from the page in both
 * themes without introducing an outlined-control grammar.
 *
 * Light and dark are deliberately asymmetric. Light uses white paper on a
 * tinted page; dark uses a near-black well beneath a lifted page. The job is
 * material separation, not literal colour sameness. A white dark-mode field
 * would be a torch.
 *
 * Grouped remains transparent and borderless: the group card is the field, and
 * drawing another outlined box inside its row would be a box inside a box.
 * `variant: "plain"` remains the explicit borderless, fill-less escape hatch.
 * Disabled fields keep an inert `fill-4` skin and `text-faint`; an outlined
 * disabled field would read as live. Invalid fields replace both material and
 * edge with the existing danger pair, so colour is still not the only signal.
 *
 * The focus ring is **not** declared here. `index.css` applies the shared 2px
 * Material teal outline globally. A text input matches `:focus-visible` whenever it is focused,
 * including by mouse, so the base rule covers every case the old per-component
 * pair covered, so controls do not re-implement it.
 */

export type ControlSize = "sm" | "md" | "lg";

/**
 * The container the control sits in, which decides how strong its fill is.
 *
 * - `panel` — an ordinary `bg-surface` panel or page. The default.
 * - `dialog` — a `bg-raised` dialog or popover.
 * - `sheet` — a **material**: a bottom sheet, the compact peek, a menu.
 *   These three share the same `field` recipe now.
 * - `grouped` — the field *is* a row of a grouped inset list, so the card is
 *   the fill and the field itself must be transparent. A tinted box inside a
 *   grouped row is a box inside a box.
 */
export type ControlContext = "panel" | "dialog" | "sheet" | "grouped";

/** Filled material field or plain (a bare row in a grouped list). */
export type ControlVariant = "filled" | "plain";

/**
 * Shape, type and transitions — everything except size and fill.
 *
 * Disabled is painted rather than faded: `opacity-45` over a filled control
 * reads as a rendering fault, while an inert fill plus `text-faint` reads as
 * "not available". `text-faint` fails AA on purpose — WCAG exempts disabled
 * controls, and anything more legible looks live.
 *
 * The placeholder is `text-muted`, **not** `text-subtle`. Both pass on the new
 * opaque field token, but the placeholder in `ui/SearchField` is also its
 * visible label, so the stronger quiet text remains the honest hierarchy.
 */
export const controlBase = cx(
  "busee-field rounded-control text-ink",
  // PS1: a framed field like the global search box, gold while focused.
  "focus:border-ps1-gold",
  "placeholder:text-muted transition-colors dur-fast",
  "pointer-coarse:min-h-tap",
  "disabled:pointer-events-none disabled:bg-fill-4 disabled:text-faint disabled:border-ps1-line",
);

/**
 * The field material by parent. Ordinary fields share one solved surface and
 * tonal fill; a grouped row deliberately owns neither.
 */
const CONTEXTS: Record<ControlContext, string> = {
  panel: "border-2 border-ps1-orange bg-field",
  dialog: "border-2 border-ps1-orange bg-field",
  sheet: "border-2 border-ps1-orange bg-field",
  grouped: "border-0 bg-transparent",
};

/**
 * Heights matched to `Button`, so a field and the button beside it line up.
 * All three rise to the 44px floor on a coarse pointer through `controlBase`.
 *
 * Height and padding are separate maps because a `plain` field drops the
 * padding and keeps the height. Two `px-*` utilities on one element are
 * resolved by Tailwind's own sheet order rather than by the order they appear
 * in the class attribute, so `px-3 px-0` is not a reliable override — the two
 * cases have to be mutually exclusive strings, not layered ones.
 */
const SIZES: Record<ControlSize, string> = {
  sm: "h-8 text-caption",
  md: "h-10 text-label",
  lg: "h-12 text-body",
};

const BOX_TYPE: Record<ControlSize, string> = {
  sm: "text-caption leading-4",
  md: "text-label leading-5",
  lg: "text-body leading-5",
};

/**
 * Padding, as four mutually exclusive strings per size rather than a base plus
 * an override.
 *
 * A field with a leading magnifier or a trailing clear ✕ needs a wider gutter
 * on that side only — and `cx("px-4", "pl-9")` cannot express that reliably,
 * because `px-*` compiles to the logical `padding-inline` shorthand and `pl-*`
 * to the physical `padding-left`, so which one wins is decided by Tailwind's
 * sheet order rather than by the order they were written. Emitting `pl-*` and
 * `pr-*` together, never alongside `px-*`, removes the question.
 */
const PADDING: Record<ControlSize, Record<"none" | "lead" | "trail" | "both", string>> = {
  sm: { none: "px-3", lead: "pl-9 pr-3", trail: "pl-3 pr-9", both: "px-9" },
  md: { none: "px-4", lead: "pl-10 pr-4", trail: "pl-4 pr-10", both: "px-10" },
  lg: { none: "px-4", lead: "pl-11 pr-4", trail: "pl-4 pr-11", both: "px-11" },
};

export type ControlOptions = {
  size?: ControlSize;
  on?: ControlContext;
  variant?: ControlVariant;
  /** Off lets a trigger shrink to its content instead of filling its parent. */
  fullWidth?: boolean;
  /**
   * Tints the field red and gives it the one boundary in this file. Pair it
   * with a message beside the field — colour alone is not a message.
   */
  invalid?: boolean;
  /** Reserves the gutter for a glyph drawn over the fill's leading edge. */
  leading?: boolean;
  /** Reserves the gutter for the clear ✕ on the trailing edge. */
  trailing?: boolean;
  className?: string;
};

/**
 * The invalid skin. A soft danger fill plus its 3:1 edge, at hairline width, so
 * the failure is carried by *fill* as well as by an outline — a red border
 * alone is invisible to a red-green deficiency on a field that is otherwise
 * grey.
 */
const INVALID = "bg-danger-soft border-2 border-danger-edge";

/**
 * Does this control have a fill of its own to inset its text from?
 *
 * A field's horizontal padding exists to hold the text off the edge of its
 * *fill*. Take the fill away and the padding becomes a second, invisible inset
 * sitting inside the one the row already has — so the value floats ~14px short
 * of the group's 16px edge while every label and separator in the list lines up
 * on it.
 *
 * `variant: "plain"` always said this. `on: "grouped"` means exactly the same
 * thing — the group card *is* the fill, which is why `CONTEXTS.grouped` is
 * `bg-transparent` — but it used to keep the padding anyway, and the two
 * spellings drifted: the workout fields said `variant:"plain", on:"grouped"` and
 * got it right, while the kanban peek said `on="grouped"` and had to cancel the
 * gutter with a `-me-3.5` at each call site. The tasks peek had the same
 * misalignment and no compensation, which is how a hand-applied fix reads when
 * it is one call site short.
 *
 * One rule now: no fill, no gutter.
 */
function bare(on: ControlContext, variant: ControlVariant): boolean {
  return variant === "plain" || on === "grouped";
}

/** Classes for an `<input>`, `<select>` or date field. */
export function control({
  size = "md",
  on = "panel",
  variant = "filled",
  fullWidth = true,
  invalid = false,
  leading = false,
  trailing = false,
  className,
}: ControlOptions = {}): string {
  const gutter = leading && trailing ? "both" : leading ? "lead" : trailing ? "trail" : "none";
  return cx(
    controlBase,
    fullWidth ? "w-full" : "w-auto",
    invalid ? INVALID : variant === "plain" ? "border-0 bg-transparent" : CONTEXTS[on],
    SIZES[size],
    bare(on, variant) ? undefined : PADDING[size][gutter],
    className,
  );
}

/** Classes for a control that sets its own height, such as a textarea. */
export function controlBox({
  size = "md",
  on = "panel",
  variant = "filled",
  invalid = false,
  className,
}: ControlOptions = {}): string {
  return cx(
    controlBase,
    "w-full",
    // Multi-line controls use the requested ramp step without inheriting the
    // single-line control's fixed height or horizontal padding.
    BOX_TYPE[size],
    invalid ? INVALID : variant === "plain" ? "border-0 bg-transparent" : CONTEXTS[on],
    className,
  );
}
